import { asyncDebounce } from '@utils/asyncDebounce';
import { REFRESH_TOKEN_URL, USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@constants/auth';
import { tokenStorage } from '@utils/tokenStorage';

const apiBaseUrl = () => import.meta.env.VITE_APP_API_URL || '/api';

/**
 * Single source of truth for JWT refresh, shared by every RTK query client AND raw
 * `fetch` callers (see authorizedFetch). Debounced so that several requests 401ing at
 * once trigger exactly ONE refresh call — concurrent callers await the same in-flight
 * promise. Returns true if the access token was refreshed, false otherwise.
 */
export const refreshAccessToken = asyncDebounce(
  async (): Promise<boolean> => {
    const refresh = tokenStorage.getToken(USER_REFRESH_TOKEN);
    if (!refresh) return false;

    try {
      const res = await fetch(`${apiBaseUrl()}/${REFRESH_TOKEN_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) return false;

      const data = await res.json();
      if (!data?.access) return false;

      tokenStorage.setToken(USER_ACCESS_TOKEN, data.access);
      // Django may rotate the refresh token; only overwrite when a new one is returned.
      if (data.refresh) tokenStorage.setToken(USER_REFRESH_TOKEN, data.refresh);
      return true;
    } catch {
      return false;
    }
  },
  5000,
  { leading: true, trailing: false },
);

/**
 * `fetch` wrapper for the few places that hit the email service directly (the Unified
 * Mailbox), giving them the same 401→refresh→retry behavior the RTK clients get.
 * Attaches the bearer token, and on a 401 refreshes once and retries the request.
 * The caller still receives the final Response (a persisting 401 means re-login needed).
 */
export const authorizedFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
  const withAuth = (): RequestInit => {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    return {
      ...init,
      headers: {
        ...(init.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  };

  let res = await fetch(input, withAuth());
  if (res.status === 401 && (await refreshAccessToken())) {
    res = await fetch(input, withAuth());
  }
  return res;
};
